import {
  DEFAULT_RELIABILITY_TYPE,
  DEFAULT_TEST_METHOD,
  DEFAULT_TRANSDUCER_TYPE
} from "constants/audiology";
import type { Report } from "services/types/reports.type";

import { calibrationDateFromConfig } from "./patient-audiology-report/patient-audiology-report-test-configuration/calibration-date.util";

export type DefaultNewReportConfig = {
  audiometer?: Report["audiometer"];
  calibrationDate?: Date | null;
  testMethod: number;
  reliability: number;
  transducer: number;
  audiometerConfiguration: Report["audiometerConfiguration"];
  reviewer: Report["reviewer"];
};

/**
 * The settings a new audiology report inherits from the patient's previous one.
 *
 * Used by all three creation paths — the New Report form, the audiology screen's quick create, and
 * Noah's "Generate Report" — through the two hooks that wrap it. It lived as two hand-kept copies
 * until SN-19223, when a fix had to be applied to both and they were already out of sync.
 *
 * The calibration date is deliberately taken from the configuration being copied and never from
 * the previous report: it describes the equipment, not the earlier test. Seeding it here is what
 * makes a new report carry the date from creation, rather than showing one the server never got.
 */
export const buildDefaultNewReportConfig = (
  latestReport?: Report
): DefaultNewReportConfig =>
  latestReport
    ? {
        audiometer: latestReport.audiometer || undefined,
        calibrationDate: calibrationDateFromConfig(
          latestReport.audiometerConfiguration
        ),
        testMethod: latestReport.testMethod || DEFAULT_TEST_METHOD,
        reliability: latestReport.reliability || DEFAULT_RELIABILITY_TYPE,
        transducer: latestReport.transducer || DEFAULT_TRANSDUCER_TYPE,
        audiometerConfiguration: latestReport.audiometerConfiguration || null,
        reviewer: latestReport.reviewer
      }
    : {
        testMethod: DEFAULT_TEST_METHOD,
        reliability: DEFAULT_RELIABILITY_TYPE,
        transducer: DEFAULT_TRANSDUCER_TYPE,
        audiometerConfiguration: null,
        reviewer: null
      };
